import { setAtPath } from './cloneAndPath';
import { type AddressGeocodeLabels } from './assetGeo';
import { geocodeAssetAddressDetails } from './resolveIsraelAddressGeo';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  return '';
}

/** Read zip/postal from nested address or asset root. */
export function pickZipFromAssetRecord(record: Record<string, unknown>): string | null {
  const addr = record.address;
  if (isPlainObject(addr)) {
    const nested =
      pickString(addr.zip_code) || pickString(addr.postal_code) || pickString(addr.zip);
    if (nested) {
      return nested;
    }
  }
  const root =
    pickString(record.zip_code) || pickString(record.postal_code) || pickString(record.zip);
  return root || null;
}

/** Set `address.zip_code` when empty — never overwrites a value the user entered. */
export function applyPostalCodeToAssetBody(
  body: Record<string, unknown>,
  postalCode: string | null | undefined,
): Record<string, unknown> {
  const zip = pickString(postalCode);
  if (!zip) {
    return body;
  }
  const addr = body.address;
  if (!isPlainObject(addr)) {
    return setAtPath(body, ['address', 'zip_code'], zip);
  }
  if (pickString(addr.zip_code)) {
    return body;
  }
  return setAtPath(body, ['address', 'zip_code'], zip);
}

/** Fill missing display zip from editor draft / GET baseline when the API omits it. */
export function enrichAssetRecordAddressForDisplay(
  display: Record<string, unknown>,
  ...sources: Array<Record<string, unknown> | null | undefined>
): Record<string, unknown> {
  if (pickZipFromAssetRecord(display)) {
    return display;
  }
  const zip =
    sources
      .filter((source): source is Record<string, unknown> => isPlainObject(source))
      .map(pickZipFromAssetRecord)
      .find((value) => value) ?? null;
  if (!zip) {
    return display;
  }
  return applyPostalCodeToAssetBody(display, zip);
}

/** Fill zip from sources, then geocode from address when still missing (display only). */
export async function enrichAssetRecordAddressForDisplayWithGeocode(
  display: Record<string, unknown>,
  labels?: AddressGeocodeLabels,
  ...sources: Array<Record<string, unknown> | null | undefined>
): Promise<Record<string, unknown>> {
  let next = enrichAssetRecordAddressForDisplay(display, ...sources);
  if (pickZipFromAssetRecord(next)) {
    return next;
  }
  const hit = await geocodeAssetAddressDetails(next, labels);
  if (hit?.postalCode) {
    next = applyPostalCodeToAssetBody(next, hit.postalCode);
  }
  return next;
}
